import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import './UserCreate.css';
import { isValidDateTime } from '../components/DateTimeInputBR';

const CompanyCreate = () => {
    const navigate = useNavigate();

    const [companyName, setCompanyName] = useState('');
    const [companyBucket, setCompanyBucket] = useState('');
    const [companySubdomain, setCompanySubdomain] = useState('');
    const [iaModels, setIaModels] = useState([]);
    const [defaultIaModel, setDefaultIaModel] = useState('');
    const [unlockDate, setUnlockDate] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        apiService.getIaModels().then((models) => setIaModels(models || []));
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });

        if (!companyName.trim() || !companyBucket.trim() || !companySubdomain.trim()) {
            setMessage({ type: 'error', text: 'Preencha todos os campos.' });
            return;
        }
        if (!defaultIaModel) {
            setMessage({ type: 'error', text: 'Selecione o modelo de IA padrão.' });
            return;
        }
        const ud = (unlockDate || '').trim();
        if (ud && !isValidDateTime(ud)) {
            setMessage({ type: 'error', text: 'Data final de aquecimento inválida. Use o formato AAAA-MM-DD HH:MM:SS (ex.: 2026-07-16 00:00:00).' });
            return;
        }

        try {
            setSubmitting(true);
            const result = await apiService.createCompany({
                companyName: companyName.trim(),
                companyBucket: companyBucket.trim(),
                companySubdomain: companySubdomain.trim(),
                default_ia_model: defaultIaModel,
                unlock_date: ud || null,
            });

            if (result.success) {
                setMessage({ type: 'success', text: `Empresa "${companyName.trim()}" cadastrada com sucesso!` });
                setCompanyName('');
                setCompanyBucket('');
                setCompanySubdomain('');
                setDefaultIaModel('');
                setUnlockDate('');
            } else {
                setMessage({ type: 'error', text: result.message || 'Erro ao cadastrar empresa.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Erro ao cadastrar empresa. Tente novamente.' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="user-create">
            <div className="user-create-header">
                <button className="btn-back" onClick={() => navigate('/users')}>
                    &#8592; Voltar
                </button>
                <h1>Cadastrar Empresa</h1>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    {message.text}
                </div>
            )}

            <div className="alert alert-warning">
                <strong>Atenção:</strong> Antes de cadastrar a empresa, certifique-se de que o schema de banco de dados já esteja criado.
            </div>

            <form className="user-create-form" onSubmit={handleSubmit}>
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="companyName">Nome da Empresa</label>
                        <input
                            id="companyName"
                            type="text"
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                            className="form-input"
                            placeholder="ex: Aliado Tecnologia"
                            autoFocus
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="companyBucket">Bucket Name</label>
                        <input
                            id="companyBucket"
                            type="text"
                            value={companyBucket}
                            onChange={(e) => setCompanyBucket(e.target.value)}
                            className="form-input"
                            placeholder="ex: aliado-bucket"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="companySubdomain">Subdomínio</label>
                        <input
                            id="companySubdomain"
                            type="text"
                            value={companySubdomain}
                            onChange={(e) => setCompanySubdomain(e.target.value)}
                            className="form-input"
                            placeholder="ex: aliado"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="defaultIaModel">Modelo de IA padrão</label>
                        <select
                            id="defaultIaModel"
                            value={defaultIaModel}
                            onChange={(e) => setDefaultIaModel(e.target.value)}
                            className="form-input"
                        >
                            <option value="">Selecione...</option>
                            {iaModels.filter((m) => !m.so_por_pergunta && !(m.so_slugs || []).length).map((m) => (
                                <option key={m.model_key} value={m.model_key}>{m.label}</option>
                            ))}
                        </select>
                        <span className="field-hint">Usado quando um prompt não define um modelo específico.</span>
                    </div>
                    {/* ------------------------------------------------------------------------------------
                        "Data final de aquecimento" (`unlock_date`) SAIU DA TELA — §2.7 do plano de Fases.

                        Aquecimento é uma FASE, não um atributo de loja/empresa: quem decide quando o
                        placar abre é a fase com `locks_scoreboard`, e o /auth passa a computar o
                        `unlock_date` a partir dela (`scoreboard_open_on`).

                        O CAMPO SÓ SAIU DA TELA — a coluna continua no banco e continua VENCENDO quem
                        não está em programa, então nada muda para as 9 lojas que têm data hoje. O
                        estado é preservado no submit: o valor atual é reenviado igual, e nunca é
                        limpo por omissão.

                        Para uma loja que precise calibrar de agora em diante, o caminho é criar
                        programa com fase de Aquecimento (que trava). Se for preciso o campo de volta
                        antes disso, é só devolver este bloco — o back-end continua aceitando.
                        ------------------------------------------------------------------------ */}
                </div>

                <div className="cls-seed-note">
                    <strong>Régua de atendimento válido</strong>
                    <p>
                        Ao cadastrar, a empresa recebe automaticamente a régua padrão a partir das
                        classificações dela: <code>valid</code>, <code>information</code>,{' '}
                        <code>directions</code> e <code>incomplete</code> contam como{' '}
                        <strong>atendimento válido</strong>; <code>invalid</code> e{' '}
                        <code>invalid_muted</code> como <strong>não-válido</strong>.
                    </p>
                    <p>
                        Para mudar isso, edite a empresa depois de criada. A alteração passa a valer
                        do dia seguinte em diante e <strong>não altera atendimento já registrado</strong>.
                    </p>
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={() => navigate('/users')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-submit"
                        disabled={submitting}
                    >
                        {submitting ? 'Cadastrando...' : 'Cadastrar Empresa'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CompanyCreate;
